/**
 * PLA-346 — bezpečný markdown → HTML pro WIDGET (vanilla TS, Shadow DOM innerHTML).
 *
 * Widget renderuje zprávy přes `innerHTML`, takže MUSÍME produkovat sanitizovaný
 * HTML string (nelze reuse react `SupportMarkdown` — tady není React). Stejná
 * bezpečnostní filozofie jako `apps/shell/src/components/support-markdown-safe.ts` —
 * `SAFE_URL_SCHEME` drž v synchronu s tím souborem.
 *
 * Strategie „escape-first" (proč je to bezpečné):
 *  1. Escapuje se CELÝ vstup → žádné raw HTML ze zdroje nepřežije
 *     (`<script>` → `&lt;script&gt;`, `"` → `&quot;`).
 *  2. Na už-escapovaném textu se aplikuje markdown subset a vkládají se JEN
 *     vlastní známé tagy (`<strong>`, `<em>`, `<a>`, `<ul>`, `<ol>`, `<li>`,
 *     `<p>`, `<br>`). Atributově nebezpečný je jen `href` — schéma jede přes
 *     allowlist a hodnota je už escapovaná (uvozovky → `&quot;`, nelze vyskočit).
 *  3. Odkazy se tokenizují (placeholdery), aby je následné regexy (autolink,
 *     bold/italic) nepřepisovaly zevnitř `href`.
 *
 * Subset: tučně (hvězdičky), kurzíva (hvězdička/podtržítko), [text](url), bare http(s) URL,
 * email→mailto, odrážky (`-`/`*`), číslování (`1.`), nové řádky. Zbytek = plain.
 */

/** Povolená URL schémata. Sync s support-markdown-safe.ts (admin react cesta). */
const SAFE_URL_SCHEME = /^(?:https?:|mailto:|tel:)/i

/** Sentinel pro tokenizaci odkazů (NUL — z reálné zprávy ho strippneme). */
const TOKEN = String.fromCharCode(0)

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&'
      ? '&amp;'
      : c === '<'
        ? '&lt;'
        : c === '>'
          ? '&gt;'
          : c === '"'
            ? '&quot;'
            : '&#39;',
  )
}

/** Vrátí url, pokud má povolené schéma (po odstranění whitespace kvůli obfuskaci), jinak null. */
function safeHref(url: string): string | null {
  return SAFE_URL_SCHEME.test(url.replace(/\s/g, '')) ? url : null
}

/** Postaví `<a>`. href + label jsou už HTML-escapované (vstup byl escapován celý). */
function anchor(href: string, label: string): string {
  const attrs = /^https?:/i.test(href) ? ' target="_blank" rel="noopener noreferrer"' : ''
  return `<a href="${href}"${attrs}>${label}</a>`
}

/** Inline formátování na JEDNOM (už escapovaném) segmentu textu. */
function inline(text: string): string {
  const tokens: string[] = []
  const stash = (html: string): string => {
    tokens.push(html)
    return `${TOKEN}${tokens.length - 1}${TOKEN}`
  }

  let s = text

  // 1) explicitní odkazy [label](url) — tokenizace (vyhne se re-matchnutí níže)
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => {
    const href = safeHref(url)
    return href ? stash(anchor(href, label)) : label
  })

  // 2) bare http(s) URL → odkaz
  s = s.replace(/(https?:\/\/[^\s<]+)/gi, (m: string) => {
    const href = safeHref(m)
    return href ? stash(anchor(href, m)) : m
  })

  // 3) bare email → mailto:
  s = s.replace(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, (m: string) =>
    stash(anchor('mailto:' + m, m)),
  )

  // 4) **tučně** (před *kurzívou*)
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')

  // 5) *kurzíva* a _kurzíva_ (jen na hranicích slov, ať neitalizuje snake_case)
  s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
  s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?:;]|$)/g, '$1<em>$2</em>')

  // restore tokenů
  return s.replace(new RegExp(`${TOKEN}(\\d+)${TOKEN}`, 'g'), (_m, i: string) => tokens[Number(i)] ?? '')
}

/**
 * Bezpečný render markdown subsetu → sanitizovaný HTML string pro innerHTML.
 * Prázdný/whitespace vstup → ''.
 */
export function renderSafeMarkdown(input: string): string {
  if (!input || !input.trim()) return ''
  // escape celého vstupu (sentinel z textu pryč, ať nekoliduje s tokenizací)
  const text = escapeHtml(input.split(TOKEN).join(''))
  const lines = text.split(/\r?\n/)

  const out: string[] = []
  let listType: 'ul' | 'ol' | null = null
  let para: string[] = []

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inline(para.join('<br>'))}</p>`)
      para = []
    }
  }
  const flushList = () => {
    if (listType) {
      out.push(`</${listType}>`)
      listType = null
    }
  }

  for (const line of lines) {
    const ul = /^\s*[-*]\s+(.*)$/.exec(line)
    const ol = /^\s*\d+\.\s+(.*)$/.exec(line)
    if (ul) {
      flushPara()
      if (listType !== 'ul') {
        flushList()
        out.push('<ul>')
        listType = 'ul'
      }
      out.push(`<li>${inline(ul[1] ?? '')}</li>`)
    } else if (ol) {
      flushPara()
      if (listType !== 'ol') {
        flushList()
        out.push('<ol>')
        listType = 'ol'
      }
      out.push(`<li>${inline(ol[1] ?? '')}</li>`)
    } else if (line.trim() === '') {
      flushPara()
      flushList()
    } else {
      flushList()
      para.push(line)
    }
  }
  flushPara()
  flushList()
  return out.join('')
}
